import HdTag from '@hellodarwin/core/lib/components/common/hd-tag';
import Link from '@hellodarwin/core/lib/components/common/link';
import { TableProps } from '@hellodarwin/core/lib/components/common/table';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import {
  ProgramGrantStatus,
  ProjectProgramWithGrant,
  getProgramGrantStatusTranslationKey,
} from '@hellodarwin/core/lib/features/entities';
import dollarFormat from '@hellodarwin/core/lib/features/helpers/dollar-format';
import { getStringDate } from '@hellodarwin/core/lib/features/helpers/get-formatted-date';
import { getProgramGrantStatusColor } from '@hellodarwin/core/lib/features/helpers/get-status-color';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Invoices from '@hellodarwin/icons/dist/icons/Invoices';
import NewWindow from '@hellodarwin/icons/dist/icons/NewWindow';
import { useCallback, useEffect, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { ProjectsSectionBlockProps } from '.';
import { useAppDispatch, useAppSelector } from '../../../../../app/index';
import {
  fetchProjectPrograms,
  selectProgramsByProjectId,
} from '../../../../../features/api/slices/projects-slice';
import { useNewClientApi } from '../../../../../features/api/use-client-api';
import { PurpleHeaderTable } from '../styles';
import {
  ApplicationsSectionContent,
  ApplicationsSectionHeader,
} from './styles';

const ApplicationsSection: React.FC<ProjectsSectionBlockProps> = ({
  project,
}) => {
  const theme = useTheme();
  const { t } = useTranslations();
  const { selectedLocale } = useLocale();
  const programs: ProjectProgramWithGrant[] = useAppSelector((state) =>
    selectProgramsByProjectId(state, project?.project_id ?? ''),
  );

  const getApplicationAmount = useCallback(
    (record: ProjectProgramWithGrant): string => {
      const amount =
        record.program_accepted_amount ??
        record.program_application_amount ??
        record.program_targeted_amount;
      return amount ? dollarFormat(amount, selectedLocale, true) : '-';
    },
    [selectedLocale],
  );

  const formatDate = useCallback(
    (value?: Date | string) => {
      if (!value) return '-';
      const dateValue = new Date(value);
      if (Number.isNaN(dateValue.getTime())) return '-';
      return getStringDate(dateValue, selectedLocale);
    },
    [selectedLocale],
  );

  const columns: TableProps<ProjectProgramWithGrant>['columns'] = useMemo(
    () => [
      {
        title: (
          <ApplicationsSectionHeader>
            <Invoices size={18} />
            {t('project_single|associated_financing')}
          </ApplicationsSectionHeader>
        ),
        dataIndex: 'program_name',
        key: 'program_name',
        render: (value: string) => (
          <Typography elementTheme="body2">{value || '-'}</Typography>
        ),
      },
      {
        title: t('application|application_status'),
        dataIndex: 'program_grant_status',
        key: 'program_grant_status',
        width: 250,
        render: (value?: ProgramGrantStatus) =>
          !!value ? (
            <HdTag
              color={getProgramGrantStatusColor(value, theme)}
              text={
                t(getProgramGrantStatusTranslationKey(value) ?? '') ||
                t(`programs|labels.${value}`)
              }
              withBorder
            />
          ) : (
            '-'
          ),
      },
      {
        title: t('application|application_amount'),
        key: 'amount',
        width: 150,
        render: (_, record) => (
          <span style={{ color: theme.colors.purple_2 }}>
            {getApplicationAmount(record)}
          </span>
        ),
      },
      {
        title: t('application|application_contact'),
        key: 'contact',
        width: 200,
        render: (_, record) => (
          <Typography elementTheme="body2">
            {record.program_contact_name || '-'}
          </Typography>
        ),
      },
      {
        title: t('application|application_created_on'),
        key: 'created_at',
        width: 180,
        render: (_, record) => formatDate(record.program_created_at),
      },
      {
        title: '',
        key: 'actions',
        width: 60,
        render: (_, record) => (
          <Link to={`/programs/${record.program_id}`}>
            <NewWindow size={18} color={theme.colors.purple_2} />
          </Link>
        ),
      },
    ],
    [formatDate, getApplicationAmount, t, theme],
  );

  const api = useNewClientApi();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (project.project_id.length > 0) {
      dispatch(
        fetchProjectPrograms({
          api,
          projectId: project.project_id,
          locale: selectedLocale,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  return (
    <ApplicationsSectionContent>
      <PurpleHeaderTable
        dataSource={programs}
        rowKey={(record) => record.program_id}
        pagination={false}
        columns={columns}
        scroll={{ x: 'max-content' }}
      />
    </ApplicationsSectionContent>
  );
};
export default ApplicationsSection;
